import { PropsWithChildren, useEffect, useState } from "react";
import { Button, InlineAlert } from '../../components/BaseComponents'
import useApi from "../../hooks/useApi";
import useAccessScope from "../../hooks/useAccessScope";
import useInterruptibleMutation from "../../hooks/useInterruptibleMutation";
import { getApiErrorMessage, getCSRFToken } from "../../lib/utils";

type BoltOnProps = PropsWithChildren<{
    label: string;
    featureKey: string;
    companyId: string;
}>

function BoltOn({label, companyId, featureKey, children}: BoltOnProps) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<any>()
    const [isAdmin, setIsAdmin] = useState(false);
    const api = useApi();
    const scopeKey = `${companyId}:${featureKey}`;
    const { generation, isCurrent } = useAccessScope(api, scopeKey);
    const [loadedGeneration, setLoadedGeneration] = useState<number>();
    const startMutation = useInterruptibleMutation(api, scopeKey,
        'The bolt-on change was interrupted and may already have been saved. Reload the organisation before trying again.');
    // Issue #884: the mount effect/onSubmit previously had no catch at all, so a rejecting GET
    // or write (including the now-rejecting READ_ONLY_ACCESS/ACCESS_AUDIT_UNAVAILABLE
    // notify-only codes - #857 item 6) produced an unhandled rejection with no visible feedback.
    const [loadError, setLoadError] = useState('');
    const [submitError, setSubmitError] = useState('');

    useEffect(() => {
        const controller = new AbortController();
        setData(undefined);
        setIsAdmin(false);
        setLoadedGeneration(generation);
        setLoading(true);
        setLoadError('');
        setSubmitError('');
        const run = async () => {
            try {
                const result = await api.get(`admin/company/${companyId}/boltOns/${featureKey}`, { signal: controller.signal })
                if(controller.signal.aborted || !isCurrent(generation)) {
                    return;
                }
                setData(result.boltOn);
                setIsAdmin(result.isAdmin);
            } catch (err) {
                if(controller.signal.aborted || !isCurrent(generation)) {
                    return;
                }
                setLoadError(getApiErrorMessage(err, `Failed to load the ${label} bolt-on. Please try again.`));
            } finally {
                if(!controller.signal.aborted && isCurrent(generation)) {
                    setLoading(false);
                }
            }
        }
        run();

        return () => controller.abort()
    }, [api, companyId, featureKey, label, generation, isCurrent])

    const onSubmit = async (enabled: boolean) => {
        if (loading || loadedGeneration !== generation || loadError) return;
        const { signal, onSubmitted, finish } = startMutation();
        setLoading(true);
        setSubmitError('');
        try {
            const csrf = await getCSRFToken(signal);
            if (signal.aborted || !isCurrent(generation)) return;
            onSubmitted();
            const result = await api.put(`admin/company/${companyId}/boltOns/${featureKey}`, {
                boltOn: { enabled },
                _csrf: csrf
            }, { signal })
            if (signal.aborted || !isCurrent(generation)) return;
            setData(result.boltOn);
        } catch (err) {
            if (!signal.aborted && isCurrent(generation)) {
                setSubmitError(getApiErrorMessage(err, `Failed to update the ${label} bolt-on. Please try again.`));
            }
        } finally {
            finish();
            if (!signal.aborted && isCurrent(generation)) setLoading(false);
        }
    }

    return (
        <div className="p-2 rounded text-white bg-charcoal-400">
            <div className="flex">
                <h4 className="w-40 text-white">{ label }</h4>
                { loadedGeneration === generation && isAdmin && !loading ? (
                    <div>Organisation is Admin</div>
                ) : (
                    <BoltOnControl
                        isActive={data?.enabled}
                        onSubmit={onSubmit}
                        loading={loading || loadedGeneration !== generation}
                        disabled={!!loadError || data === undefined}
                    />
                )}
            </div>
            { loadedGeneration === generation && loadError && <InlineAlert type='error' className='mt-2'>{ loadError }</InlineAlert> }
            { loadedGeneration === generation && submitError && <InlineAlert type='error' className='mt-2'>{ submitError }</InlineAlert> }
            <div className="mt-2">
                { children }
            </div>
        </div>
    );
}

type BoltOnControlProps = {
    isActive: boolean;
    onSubmit: (enabled: boolean) => void,
    loading?: boolean;
    disabled?: boolean;
}

function BoltOnControl({isActive, onSubmit, loading, disabled}: BoltOnControlProps) {
    if (loading) {
        return <Button type="Secondary" size="sm" text="Loading..." disabled />
    }
    if((isActive)) {
        return (
            <div className="flex space-x-2">
                <Button type="Primary" size="sm" text="Enabled" disabled />
                <Button type="Outlined" size="sm" text="Disable" disabled={disabled} onClick={() => onSubmit(false)} />
            </div>
        );
    }

    return (
        <div className="flex space-x-2">
            <Button type="Outlined" size="sm" text="Enable" disabled={disabled} onClick={() => onSubmit(true)} />
            <Button type="Primary" size="sm" text="Disabled" disabled additionalClass="!bg-error-500 !border-error-500 !text-error-200" />
        </div>
    );
}

type AdminCompanyBoltOnsProps = {
    companyId: string;
}

// Keys must match FEATURE_BOLT_ON_KEYS in backend/app/constants/featureBoltOns.ts.
export const BOLT_ONS = [
    { featureKey: 'portfolios', label: 'Portfolios', description: 'Curate portfolios.' },
    { featureKey: 'client_accounting', label: 'Client Accounting', description: 'Manage carbon accounting data for clients companies.' },
    { featureKey: 'projects', label: 'Projects', description: 'Group emissions for projects.' },
    { featureKey: 'company_groups', label: 'Company Groups', description: 'Not yet wired up: switching this on or off has no effect on the organisation for now.' },
    { featureKey: 'screen_scrape', label: 'Screen-scraping', description: 'Can select & copy data from reports.' },
]

function AdminCompanyBoltOns({companyId}: AdminCompanyBoltOnsProps) {

    return (
        <div>
            <h3 className="text-white mb-3 mt-8">Bolt Ons</h3>
            <div className="rounded bg-charcoal-600 p-5 space-y-5">
                {BOLT_ONS.map(({featureKey, label, description}) => (
                    <BoltOn key={featureKey} companyId={companyId} label={label} featureKey={featureKey}>
                        <p>{description}</p>
                    </BoltOn>
                ))}
            </div>
        </div>
    )
}

export default AdminCompanyBoltOns;
